import { Button } from '@mui/material';
import { useState } from 'react';
import { useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

const GoogleAuthButton = () => {
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  const navigate = useNavigate();

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      // Step 1: Get the Google OAuth URL from the backend
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/google/redirect`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to get Google OAuth URL');
      }

      const data = await response.json();
      const googleAuthUrl = data.url;

      // Step 2: Open Google OAuth in a popup window
      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        googleAuthUrl,
        'Google OAuth',
        `width=${width},height=${height},left=${left},top=${top}`,
      );

      if (!popup) {
        throw new Error('Popup blocked. Please allow popups for this site.');
      }

      // Step 3: Listen for the OAuth callback
      const handleMessage = async (event: MessageEvent) => {
        console.log('Message received:', event);
        console.log('Event origin:', event.origin);
        console.log('Window origin:', window.location.origin);
        console.log('Event data:', event.data);
        console.log('Event data type:', event.data?.type);

        // Skip non-OAuth messages
        if (
          !event.data ||
          !event.data.type ||
          (event.data.type !== 'GOOGLE_OAUTH_SUCCESS' && event.data.type !== 'GOOGLE_OAUTH_ERROR')
        ) {
          console.log('Not a Google OAuth message, ignoring');
          return;
        }

        // Verify origin for security - accept from backend or same origin
        const backendOrigin = new URL(process.env.REACT_APP_API_URL || '').origin;
        console.log('Expected backend origin:', backendOrigin);

        if (event.origin !== window.location.origin && event.origin !== backendOrigin) {
          console.warn('Origin mismatch - message ignored');
          console.warn('Expected:', window.location.origin, 'or', backendOrigin);
          console.warn('Received:', event.origin);
          // Temporarily allow for debugging - REMOVE IN PRODUCTION
          console.warn('Allowing message anyway for debugging...');
        }

        if (event.data.type === 'GOOGLE_OAUTH_SUCCESS') {
          console.log('Google OAuth success message received');
          window.removeEventListener('message', handleMessage);
          popup?.close();

          try {
            const { data: tokenData } = event.data;
            console.log('Token data:', tokenData);

            if (!tokenData || !tokenData.access_token) {
              throw new Error('Invalid authentication response');
            }

            // Step 4: Save tokens and extract permissions from JWT
            localStorage.setItem('token', tokenData.access_token);
            localStorage.setItem('refresh', tokenData.refresh_token || '');

            // Calculate expiration time (expires_in is in seconds)
            const expiresAt = new Date().getTime() + tokenData.expires_in * 1000;
            localStorage.setItem('expires', expiresAt.toString());

            // Decode JWT to extract permissions
            const decoded = jwtDecode<{ permissions: string[] }>(tokenData.access_token);
            localStorage.setItem('permissions', JSON.stringify(decoded.permissions || []));

            console.log('Tokens and permissions saved, redirecting to dashboard...');

            // Navigate to dashboard
            navigate('/');
            notify('Successfully logged in with Google', { type: 'success' });
          } catch (error) {
            console.error('Error processing OAuth success:', error);
            notify('Failed to complete Google authentication', { type: 'error' });
            console.error('Google OAuth error:', error);
          } finally {
            setLoading(false);
          }
        } else if (event.data.type === 'GOOGLE_OAUTH_ERROR') {
          console.log('Google OAuth error message received');
          window.removeEventListener('message', handleMessage);
          popup?.close();
          setLoading(false);
          notify(event.data.error || 'Google authentication cancelled or failed', { type: 'error' });
        }
      };

      console.log('Adding message event listener');
      window.addEventListener('message', handleMessage);

      // Cleanup listener after 5 minutes
      setTimeout(() => {
        window.removeEventListener('message', handleMessage);
        if (popup && !popup.closed) {
          popup.close();
        }
        setLoading(false);
      }, 300000);
    } catch (error) {
      setLoading(false);
      notify(error instanceof Error ? error.message : 'Failed to initiate Google login', {
        type: 'error',
      });
      console.error('Google OAuth error:', error);
    }
  };

  return (
    <Button
      variant="contained"
      fullWidth
      onClick={handleGoogleLogin}
      disabled={loading}
      sx={{
        'backgroundColor': '#f1be48',
        'color': '#081f2c',
        'border': '2px solid #f1be48',
        'borderRadius': '30px',
        'borderTopRightRadius': '10px',
        'textTransform': 'none',
        'fontWeight': 600,
        'fontSize': '1rem',
        'padding': '5px',
        'boxShadow': 'none',
        '&:hover': {
          backgroundColor: 'white',
          borderColor: '#081f2c',
          color: '#081f2c',
          boxShadow: 'none',
        },
        '&:disabled': {
          backgroundColor: 'rgba(241, 190, 72, 0.5)',
          borderColor: 'rgba(241, 190, 72, 0.5)',
          color: 'rgba(8, 31, 44, 0.5)',
        },
      }}
    >
      <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: 8 }}>
        <g fill="none" fillRule="evenodd">
          <path d="M17.6 9.2l-.1-1.8H9v3.4h4.8C13.6 13 13 13.8 12.1 14.4v2.8h3c2-1.8 3.1-4.5 3.1-7.7z" fill="#4285F4" />
          <path
            d="M9 18c2.4 0 4.5-.8 6-2.2l-3-2.8c-.8.5-1.8.8-3 .8-2.3 0-4.3-1.6-5-3.7H1v2.9C2.5 16.3 5.5 18 9 18z"
            fill="#34A853"
          />
          <path d="M4 10.7c-.2-.5-.2-1.1 0-1.7V6.1H1C.4 7.3 0 8.6 0 10s.4 2.7 1 3.9l3-2.9z" fill="#FBBC05" />
          <path
            d="M9 3.6c1.3 0 2.5.4 3.4 1.3L15 2.3C13.5.9 11.4 0 9 0 5.5 0 2.5 1.7 1 4.6l3 2.9c.7-2.1 2.7-3.7 5-3.7z"
            fill="#EA4335"
          />
        </g>
      </svg>
      {loading ? 'Signing in...' : 'Sign in with Google'}
    </Button>
  );
};

export default GoogleAuthButton;
